// GridGeometry - Types and geometry operations for Grid Games

// === Primitive Types ===

/** Edge orientation: vertical or horizontal */
export type Orientation = 'v' | 'h';

/** Cardinal direction (north is always up) */
export type Direction = 'N' | 'S' | 'W' | 'E';

/**
 * Grid intersection point (corners of cells).
 * 0 <= row, col <= size (inclusive on both ends)
 */
export interface Point {
    row: number;
    col: number;
}

/**
 * Cell position (center of a cell, inside grid).
 * 0 <= row, col < size
 */
export interface Position {
    row: number;
    col: number;
}

/**
 * Outside position (adjacent to grid but outside).
 * One of row/col is -1 or size, the other is 0 to size-1.
 * `side` indicates which grid edge it's beyond.
 */
export interface Outside {
    row: number;
    col: number;
    side: Direction;
}

/**
 * Edge between two cells (or between a cell and outside).
 * pa, pb: the two endpoints (Points) of the edge, normalized (by col then row)
 * ca, cb: the two cells/outside regions this edge separates
 * orient: whether the edge runs horizontally or vertically
 */
export interface Edge {
    pa: Point;
    pb: Point;
    ca: Position | Outside;
    cb: Position | Outside;
    orient: Orientation;
}

// === Direction Helpers ===

/** Get opposite direction */
export function oppositeDir(dir: Direction): Direction {
    const opposites: Record<Direction, Direction> = {
        'N': 'S', 'S': 'N', 'E': 'W', 'W': 'E'
    };
    return opposites[dir];
}

/**
 * Direction of the step from one cell/point to an orthogonally adjacent one, or
 * null if they aren't a single step apart. Rows grow downward, so +row is South.
 */
export function directionBetween(from: Position, to: Position): Direction | null {
    const dr = to.row - from.row;
    const dc = to.col - from.col;
    if (dr === -1 && dc === 0) return 'N';
    if (dr === 1 && dc === 0) return 'S';
    if (dr === 0 && dc === -1) return 'W';
    if (dr === 0 && dc === 1) return 'E';
    return null;
}

/** Turn clockwise */
export function turnCW(dir: Direction): Direction {
    const turns: Record<Direction, Direction> = {
        'N': 'E', 'E': 'S', 'S': 'W', 'W': 'N'
    };
    return turns[dir];
}

/** Turn counter-clockwise */
export function turnCCW(dir: Direction): Direction {
    const turns: Record<Direction, Direction> = {
        'N': 'W', 'W': 'S', 'S': 'E', 'E': 'N'
    };
    return turns[dir];
}

/** Get orientation of movement in a direction (N/S move vertically, E/W move horizontally) */
export function dirOrientation(dir: Direction): Orientation {
    return (dir === 'N' || dir === 'S') ? 'v' : 'h';
}

// === Point Helpers ===

/**
 * Serialize point to string format (col then row).
 * Uses X for 10.
 * Examples: "00", "43", "7X", "X9"
 */
function serializePoint(p: Point): string {
    const colChar = p.col === 10 ? 'X' : p.col.toString();
    const rowChar = p.row === 10 ? 'X' : p.row.toString();
    return colChar + rowChar;
}

/**
 * Deserialize point from string format.
 * Examples: "00" -> {row:0,col:0}, "7X" -> {row:10,col:7}
 */
function deserializePoint(s: string): Point {
    if (s.length !== 2) throw new Error(`Invalid point format: ${s}`);
    const colChar = s[0];
    const rowChar = s[1];
    const col = colChar === 'X' ? 10 : parseInt(colChar, 10);
    const row = rowChar === 'X' ? 10 : parseInt(rowChar, 10);
    return { row, col };
}

/** Get unique key for a point */
export function pointKey(p: Point): string {
    return serializePoint(p);
}

/** Parse point key back to Point */
export function parsePointKey(key: string): Point {
    return deserializePoint(key);
}

/** Check if two points are equal */
export function pointsEqual(a: Point, b: Point): boolean {
    return a.row === b.row && a.col === b.col;
}

/** Manhattan distance between two points */
export function pointDistance(a: Point, b: Point): number {
    return Math.abs(a.row - b.row) + Math.abs(a.col - b.col);
}

// === Position Helpers ===

/** Get unique key for a position */
export function positionKey(pos: Position): string {
    return `${pos.row},${pos.col}`;
}

/** Parse position key back to Position */
export function parsePositionKey(key: string): Position {
    const [row, col] = key.split(',').map(Number);
    return { row, col };
}

/** Check if two positions are equal */
export function positionsEqual(a: Position, b: Position): boolean {
    return a.row === b.row && a.col === b.col;
}

/** Manhattan distance between two positions */
export function manhattanDistance(a: Position, b: Position): number {
    return Math.abs(a.row - b.row) + Math.abs(a.col - b.col);
}

/** Type guard: check if a Position | Outside is inside grid */
export function isInside(pos: Position | Outside): pos is Position {
    return !('side' in pos);
}

/** Type guard: check if a Position | Outside is outside grid */
export function isOutside(pos: Position | Outside): pos is Outside {
    return 'side' in pos;
}

// === Edge Helpers (standalone, non-size-aware) ===

/** Normalize point order for edge (by col then row) */
export function normalizePointPair(a: Point, b: Point): [Point, Point] {
    if (a.col < b.col || (a.col === b.col && a.row < b.row)) {
        return [a, b];
    }
    return [b, a];
}

/** Get edge key (using serialized point format) */
export function edgeKey(edge: Edge): string {
    return serializePoint(edge.pa) + serializePoint(edge.pb);
}

/** Check if edge is on the rim (grid boundary) */
export function isRimEdge(edge: Edge): boolean {
    return isOutside(edge.ca) || isOutside(edge.cb);
}

/** Get cells adjacent to an edge (only those inside grid) */
export function edgeAdjacentCells(edge: Edge): Position[] {
    const cells: Position[] = [];
    if (isInside(edge.ca)) cells.push(edge.ca);
    if (isInside(edge.cb)) cells.push(edge.cb);
    return cells;
}

/** Check if two edges are equal (same endpoints) */
export function edgesEqual(a: Edge, b: Edge): boolean {
    return edgeKey(a) === edgeKey(b);
}

// === GridGeometry Class (size-aware operations) ===

/**
 * Size-aware geometry operations for grids.
 * Handles coordinate validation, edge creation, and edge deserialization.
 */
export class GridGeometry {
    constructor(readonly size: number) {}

    // === Coordinate Methods ===

    /** Check if position is valid (inside grid). */
    isValidPosition(pos: Position): boolean {
        return pos.row >= 0 && pos.row < this.size &&
               pos.col >= 0 && pos.col < this.size;
    }

    /** Check if point is valid (within grid intersections). */
    isValidPoint(p: Point): boolean {
        return p.row >= 0 && p.row <= this.size &&
               p.col >= 0 && p.col <= this.size;
    }

    /** Check if point is on rim (grid boundary). */
    isRimPoint(p: Point): boolean {
        return p.row === 0 || p.row === this.size ||
               p.col === 0 || p.col === this.size;
    }

    /** Check if point is a corner of the grid. */
    isCornerPoint(p: Point): boolean {
        return (p.row === 0 || p.row === this.size) &&
               (p.col === 0 || p.col === this.size);
    }

    /** Every rim intersection, in row-then-column order. Corners are excluded by
     *  default (they rarely make valid starts/anchors). */
    rimPoints(includeCorners = false): Point[] {
        const pts: Point[] = [];
        for (let c = 0; c <= this.size; c++) {
            pts.push({ row: 0, col: c });
            pts.push({ row: this.size, col: c });
        }
        for (let r = 1; r < this.size; r++) {
            pts.push({ row: r, col: 0 });
            pts.push({ row: r, col: this.size });
        }
        return includeCorners ? pts : pts.filter(p => !this.isCornerPoint(p));
    }

    /** The direction pointing into the grid from a non-corner rim point; null for
     *  corners and interior points. */
    rimInwardDirection(p: Point): Direction | null {
        if (this.isCornerPoint(p) || !this.isRimPoint(p)) return null;
        if (p.row === 0) return 'S';
        if (p.row === this.size) return 'N';
        if (p.col === 0) return 'E';
        return 'W';
    }

    /** Get the opposite rim point for wrapping (null if not on rim or at corner). */
    getWrapPoint(p: Point): Point | null {
        if (p.row === 0 && p.col > 0 && p.col < this.size) {
            return { row: this.size, col: p.col };
        }
        if (p.row === this.size && p.col > 0 && p.col < this.size) {
            return { row: 0, col: p.col };
        }
        if (p.col === 0 && p.row > 0 && p.row < this.size) {
            return { row: p.row, col: this.size };
        }
        if (p.col === this.size && p.row > 0 && p.row < this.size) {
            return { row: p.row, col: 0 };
        }
        return null;
    }

    /**
     * Move position in a direction.
     * Returns Position if staying inside grid, Outside if exiting.
     */
    move(pos: Position, dir: Direction): Position | Outside {
        const newRow = pos.row + (dir === 'S' ? 1 : dir === 'N' ? -1 : 0);
        const newCol = pos.col + (dir === 'E' ? 1 : dir === 'W' ? -1 : 0);

        if (newRow < 0) return { row: -1, col: pos.col, side: 'N' };
        if (newRow >= this.size) return { row: this.size, col: pos.col, side: 'S' };
        if (newCol < 0) return { row: pos.row, col: -1, side: 'W' };
        if (newCol >= this.size) return { row: pos.row, col: this.size, side: 'E' };

        return { row: newRow, col: newCol };
    }

    /**
     * Move position in direction, staying inside grid.
     * Returns null if would exit grid.
     */
    moveInside(pos: Position, dir: Direction): Position | null {
        const result = this.move(pos, dir);
        return isInside(result) ? result : null;
    }

    /**
     * Check if an internal edge touches the rim (has at least one endpoint on the boundary).
     */
    edgeTouchesRim(edge: { pa: Point; pb: Point }): boolean {
        return this.isRimPoint(edge.pa) || this.isRimPoint(edge.pb);
    }

    // === Edge Creation Methods ===

    /** Create edge between two adjacent points (size-aware). */
    createEdge(p1: Point, p2: Point): Edge | null {
        const rowDiff = Math.abs(p1.row - p2.row);
        const colDiff = Math.abs(p1.col - p2.col);

        // Points must be adjacent (differ by 1 in exactly one dimension)
        if (!((rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1))) {
            return null;
        }

        const [pa, pb] = normalizePointPair(p1, p2);
        const orient: Orientation = colDiff === 0 ? 'v' : 'h';

        let ca: Position | Outside;
        let cb: Position | Outside;

        if (orient === 'v') {
            // Vertical edge: cells are left and right
            const leftCol = Math.min(pa.col, pb.col) - 1;
            const rightCol = leftCol + 1;
            const row = pa.row;

            ca = leftCol < 0
                ? { row, col: -1, side: 'W' as Direction }
                : { row, col: leftCol };
            cb = rightCol >= this.size
                ? { row, col: this.size, side: 'E' as Direction }
                : { row, col: rightCol };
        } else {
            // Horizontal edge: cells are above and below
            const topRow = Math.min(pa.row, pb.row) - 1;
            const bottomRow = topRow + 1;
            const col = pa.col;

            ca = topRow < 0
                ? { row: -1, col, side: 'N' as Direction }
                : { row: topRow, col };
            cb = bottomRow >= this.size
                ? { row: this.size, col, side: 'S' as Direction }
                : { row: bottomRow, col };
        }

        return { pa, pb, ca, cb, orient };
    }

    /**
     * Get the edge from a position in a given direction.
     * Returns the edge that separates this cell from the adjacent cell (or outside) in that direction.
     */
    getEdgeFromPosition(pos: Position, dir: Direction): Edge {
        const neighbor = this.move(pos, dir);
        const edge = this.createEdgeFromCells(pos, neighbor);
        if (!edge) {
            throw new Error(`Cannot create edge from ${positionKey(pos)} in direction ${dir}`);
        }
        return edge;
    }

    /**
     * Create an edge from two adjacent cells (Position or Outside).
     */
    createEdgeFromCells(ca: Position | Outside, cb: Position | Outside): Edge | null {
        const insideA = isInside(ca) ? ca : null;
        const insideB = isInside(cb) ? cb : null;

        if (insideA && insideB) {
            // Both inside: standard case
            const rowDiff = insideB.row - insideA.row;
            const colDiff = insideB.col - insideA.col;

            if (Math.abs(rowDiff) === 1 && colDiff === 0) {
                // Horizontal edge: cells are vertically adjacent
                const topRow = Math.min(insideA.row, insideB.row);
                return this.createEdge(
                    { row: topRow + 1, col: insideA.col },
                    { row: topRow + 1, col: insideA.col + 1 }
                );
            } else if (rowDiff === 0 && Math.abs(colDiff) === 1) {
                // Vertical edge: cells are horizontally adjacent
                const leftCol = Math.min(insideA.col, insideB.col);
                return this.createEdge(
                    { row: insideA.row, col: leftCol + 1 },
                    { row: insideA.row + 1, col: leftCol + 1 }
                );
            }
            throw new Error('Cells are not adjacent');
        }

        // One inside, one outside (rim edge)
        const inside = insideA ?? insideB;
        const outside = (isOutside(ca) ? ca : cb) as Outside;

        if (!inside) {
            throw new Error('Cannot create edge with both cells outside');
        }

        // Determine edge points based on which side the outside is on
        switch (outside.side) {
            case 'N':
                return this.createEdge(
                    { row: inside.row, col: inside.col },
                    { row: inside.row, col: inside.col + 1 }
                );
            case 'S':
                return this.createEdge(
                    { row: inside.row + 1, col: inside.col },
                    { row: inside.row + 1, col: inside.col + 1 }
                );
            case 'W':
                return this.createEdge(
                    { row: inside.row, col: inside.col },
                    { row: inside.row + 1, col: inside.col }
                );
            case 'E':
                return this.createEdge(
                    { row: inside.row, col: inside.col + 1 },
                    { row: inside.row + 1, col: inside.col + 1 }
                );
        }
    }

    /**
     * Get all edges meeting at a point (up to 4 edges, one in each direction).
     */
    getEdgesAtPoint(point: Point): Edge[] {
        const edges: Edge[] = [];

        const offsets = [
            { dr: -1, dc: 0 },  // up (vertical edge)
            { dr: 1, dc: 0 },   // down (vertical edge)
            { dr: 0, dc: -1 },  // left (horizontal edge)
            { dr: 0, dc: 1 }    // right (horizontal edge)
        ];

        for (const { dr, dc } of offsets) {
            const otherPoint: Point = { row: point.row + dr, col: point.col + dc };
            if (this.isValidPoint(otherPoint)) {
                const edge = this.createEdge(point, otherPoint);
                if (edge) {
                    edges.push(edge);
                }
            }
        }

        return edges;
    }

    /**
     * Get all edges that share a point with this edge (i.e., edges touching this edge).
     * Returns up to 6 edges: up to 3 at each endpoint.
     */
    getEdgesTouchingEdge(edge: Edge): Edge[] {
        const result: Edge[] = [];

        for (const point of [edge.pa, edge.pb]) {
            const adjacentEdges = this.getEdgesAtPoint(point);
            for (const adjEdge of adjacentEdges) {
                if (edgeKey(adjEdge) !== edgeKey(edge)) {
                    if (!result.some(e => edgeKey(e) === edgeKey(adjEdge))) {
                        result.push(adjEdge);
                    }
                }
            }
        }

        return result;
    }

    // === Edge Deserialization Methods (size-aware) ===

    /**
     * Deserialize edge from point-based format (size-aware).
     * Example: "0001" -> edge from point (0,0) to point (0,1)
     */
    deserializeEdge(s: string): Edge | null {
        if (s.length !== 4) throw new Error(`Invalid edge format: ${s}`);
        const pa = deserializePoint(s.slice(0, 2));
        const pb = deserializePoint(s.slice(2, 4));
        return this.createEdge(pa, pb);
    }

    /**
     * Deserialize position from string format.
     * Examples: "A0" -> {row:0,col:0}
     */
    private deserializePosition(s: string): Position {
        if (s.length !== 2) throw new Error(`Invalid position format: ${s}`);
        const col = s.charCodeAt(0) - 'A'.charCodeAt(0);
        const row = parseInt(s[1], 10);
        return { row, col };
    }

    /**
     * Parse outside-with-cell format using this.size.
     * Example: "oNA0" -> { outside: Outside north of A0, cell: Position A0 }
     */
    private parseOutsideWithCell(s: string): { outside: Outside; cell: Position } {
        if (s.length !== 4 || s[0] !== 'o') {
            throw new Error(`Invalid outside-with-cell format: ${s}`);
        }
        const side = s[1] as Direction;
        if (!['N', 'S', 'E', 'W'].includes(side)) {
            throw new Error(`Invalid direction in outside format: ${side}`);
        }
        const cell = this.deserializePosition(s.slice(2));

        let outside: Outside;
        switch (side) {
            case 'N':
                outside = { row: -1, col: cell.col, side: 'N' };
                break;
            case 'S':
                outside = { row: this.size, col: cell.col, side: 'S' };
                break;
            case 'W':
                outside = { row: cell.row, col: -1, side: 'W' };
                break;
            case 'E':
                outside = { row: cell.row, col: this.size, side: 'E' };
                break;
        }

        return { outside, cell };
    }

    /**
     * Deserialize edge from cell-based format (size-aware).
     * Internal: "A6A7" -> edge between cells A6 and A7
     * Rim: "oNA0" -> north edge of A0 (between outside and A0)
     */
    deserializeEdgeCells(s: string): Edge | null {
        // Check for outside-with-cell format (starts with 'o')
        if (s.length === 4 && s[0] === 'o') {
            const { outside, cell } = this.parseOutsideWithCell(s);
            return this.createEdgeFromCells(outside, cell);
        }

        // Standard internal format: two positions
        if (s.length !== 4) throw new Error(`Invalid edge format: ${s}`);
        const ca = this.deserializePosition(s.slice(0, 2));
        const cb = this.deserializePosition(s.slice(2, 4));

        return this.createEdgeFromCells(ca, cb);
    }
}
